import { s } from '@/theme/tokens';
import { Box, Surface, SurfaceButton } from '@/ui/primitives';
import { MetricCardTitle } from './MetricCardTitle';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement, ReactNode } from 'react';

type ResponsiveStyleValue = string | number | Readonly<Record<string, string | number>>;
type MetricTrendCardFrameSx = Exclude<Extract<SxProps<Theme>, readonly unknown[]>[number], boolean>;

type MetricTrendCardFrameInteraction = {
  selected: boolean;
  onClick: () => void;
  focusColor: string;
};

type MetricTrendCardFrameProps = {
  title: ReactNode;
  cornerIcon?: ReactNode;
  infoContent?: ReactNode;
  leftAccentColor?: string;
  value: ReactNode;
  chart: ReactNode;
  footer: ReactNode;
  minHeight: number;
  chartHeight: number;
  titleFontSize?: string | { xs?: number; xl?: number };
  showInfoIcon?: boolean;
  fixedHeight?: boolean;
  hideCornerIconBelowWidth?: number;
  headerPaddingX: ResponsiveStyleValue;
  headerPaddingTop?: string;
  headerPaddingBottom?: string;
  middleGridTemplateColumns: ResponsiveStyleValue;
  middleColumnGap?: string;
  middlePaddingY?: string;
  valuePaddingX: ResponsiveStyleValue;
  valuePaddingRight?: ResponsiveStyleValue;
  valuePaddingY?: string;
  chartPaddingX: ResponsiveStyleValue;
  chartPaddingLeft?: ResponsiveStyleValue;
  footerPaddingX: ResponsiveStyleValue;
  footerPaddingTop?: string;
  footerPaddingBottom: string;
  footerSx?: MetricTrendCardFrameSx;
  sx?: MetricTrendCardFrameSx;
  interaction?: MetricTrendCardFrameInteraction;
};

export const MetricTrendCardFrame = ({
  title,
  cornerIcon,
  infoContent,
  leftAccentColor,
  value,
  chart,
  footer,
  minHeight,
  chartHeight,
  titleFontSize,
  showInfoIcon = false,
  fixedHeight = false,
  hideCornerIconBelowWidth,
  headerPaddingX,
  headerPaddingTop = s('lg'),
  headerPaddingBottom = s('xxs'),
  middleGridTemplateColumns,
  middleColumnGap = s('xs'),
  middlePaddingY = s('xs'),
  valuePaddingX,
  valuePaddingRight,
  valuePaddingY = s('xxs'),
  chartPaddingX,
  chartPaddingLeft,
  footerPaddingX,
  footerPaddingTop = s('xs'),
  footerPaddingBottom,
  footerSx,
  sx,
  interaction,
}: MetricTrendCardFrameProps): ReactElement => {
  const cardContent = (
    <Box
      data-metric-trend-card-frame="true"
      sx={[
        {
          height: '100%',
          boxSizing: 'border-box',
          display: 'grid',
          gridTemplateRows: `auto minmax(${chartHeight}px, max-content) auto`,
          alignContent: 'center',
          alignItems: 'stretch',
          position: 'relative',
        },
        hideCornerIconBelowWidth === undefined ? null : { containerType: 'inline-size' },
      ]}
    >
      {cornerIcon ? (
        <Box
          sx={[
            {
              position: 'absolute',
              top: '4px',
              right: '4px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              opacity: 0.78,
            },
            hideCornerIconBelowWidth === undefined
              ? null
              : {
                  [`@container (max-width: ${String(hideCornerIconBelowWidth)}px)`]: {
                    display: 'none',
                  },
                },
          ]}
        >
          {cornerIcon}
        </Box>
      ) : null}
      <Box
        sx={{
          minWidth: 0,
          px: headerPaddingX,
          pt: headerPaddingTop,
          pb: headerPaddingBottom,
        }}
      >
        <MetricCardTitle
          fontSize={titleFontSize}
          infoContent={infoContent}
          showInfoIcon={showInfoIcon}
        >
          {title}
        </MetricCardTitle>
      </Box>
      <Box
        sx={{
          minWidth: 0,
          display: 'grid',
          gridTemplateColumns: middleGridTemplateColumns,
          columnGap: middleColumnGap,
          alignItems: 'center',
          minHeight: chartHeight,
          py: middlePaddingY,
        }}
      >
        <Box
          sx={{
            minWidth: 0,
            pl: valuePaddingX,
            pr: valuePaddingRight ?? valuePaddingX,
            py: valuePaddingY,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {value}
        </Box>
        <Box
          sx={{
            minWidth: 0,
            px: chartPaddingX,
            pl: chartPaddingLeft,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            opacity: 0.64,
          }}
        >
          {chart}
        </Box>
      </Box>
      <Box
        sx={[
          {
            minWidth: 0,
            px: footerPaddingX,
            pt: footerPaddingTop,
            pb: footerPaddingBottom,
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'wrap',
          },
          ...(footerSx ? [footerSx] : []),
        ]}
      >
        {footer}
      </Box>
    </Box>
  );

  const surfaceSx: MetricTrendCardFrameSx = {
    minHeight: `${minHeight}px`,
    boxSizing: 'border-box',
    overflow: 'visible',
  };
  const leftAccentSx: MetricTrendCardFrameSx | null = leftAccentColor
    ? {
        borderLeft: `2px solid ${leftAccentColor}`,
      }
    : null;
  const fixedHeightSx: MetricTrendCardFrameSx = {
    height: `${minHeight}px`,
  };

  if (interaction) {
    return (
      <SurfaceButton
        type="button"
        onClick={interaction.onClick}
        aria-pressed={interaction.selected}
        padding="none"
        radius="md"
        sx={[
          surfaceSx,
          leftAccentSx,
          ...(sx ? [sx] : []),
          ...(fixedHeight ? [fixedHeightSx] : []),
          {
            cursor: 'pointer',
            '&:focus-visible': {
              outline: `2px solid ${interaction.focusColor}`,
              outlineOffset: s('xxs'),
            },
          },
          interaction.selected
            ? {
                outline: `1px solid ${interaction.focusColor}`,
                outlineOffset: '-1px',
              }
            : null,
        ]}
      >
        {cardContent}
      </SurfaceButton>
    );
  }

  return (
    <Surface
      padding="none"
      radius="md"
      sx={[surfaceSx, leftAccentSx, ...(sx ? [sx] : []), ...(fixedHeight ? [fixedHeightSx] : [])]}
    >
      {cardContent}
    </Surface>
  );
};
